import { AxiosError } from 'axios';
import { HttpStatus, UserPayload } from '@monorepo/client-types';
import { CommonErrors } from '@monorepo/types';
import { BaseResponse } from '../core/base-entity-service';
import CrudService from '../core/crud-service';

export class AuthenticationService extends CrudService {
  static override route = 'authentication';

  async descopeLogin(body?: { registeredViaContestSignup?: boolean }) {
    try {
      // Note: Descope session token is automatically added to Authorization header
      // by http-service interceptor. The backend extracts it from the header via JwtAuthGuard.
      const response: {
        data: { isNewUser?: boolean; tokenInSync?: boolean };
      } = await this.httpService.post(`${this.path}/descope-auth`, body ?? {});
      return response.data;
    } catch (e) {
      this.handleLoginError(e);
    }
  }

  async refreshToken() {
    try {
      await this.httpService.post(`${this.path}/refresh-token`);
    } catch (e) {
      console.error('Failed refreshing token', e);
      throw new Error(CommonErrors.FAILED_REFRESH_TOKEN);
    }
  }

  handleLoginError(error: unknown) {
    if (error instanceof AxiosError) {
      const responseError = error.response?.data;
      if (
        [HttpStatus.BAD_REQUEST, HttpStatus.UNAUTHORIZED].includes(
          responseError?.statusCode,
        )
      ) {
        throw new Error(error.response?.data.message);
      }
      throw new Error(CommonErrors.SOMETHING_WRONG);
    }
    if (error instanceof Error) {
      throw new Error(error?.message || CommonErrors.SOMETHING_WRONG);
    }
    throw new Error(CommonErrors.SOMETHING_WRONG);
  }

  async registerUser(
    userDetails: UserPayload,
  ): Promise<{ message: string; userId: string }> {
    try {
      const response = await this.httpService.post<
        UserPayload,
        { data: { message: string; userId: string } }
      >(`${this.path}/register`, userDetails);
      return response.data;
    } catch (e) {
      throw new Error(
        e instanceof Error ? e.message : CommonErrors.SOMETHING_WRONG,
      );
    }
  }

  async otpRegister(
    userDetails: UserPayload,
    options?: { registeredViaContestSignup?: boolean },
  ): Promise<{ userId: string } | undefined> {
    try {
      const body = { ...userDetails, ...options };
      const response: { data: { message: string; userId: string } } =
        await this.httpService.post(`${this.path}/otp-register`, body);
      return response.data;
    } catch (e) {
      throw new Error(
        e instanceof Error ? e.message : CommonErrors.SOMETHING_WRONG,
      );
    }
  }

  async logout(): Promise<string> {
    try {
      const response = await this.httpService.post<void, BaseResponse<string>>(
        `${this.path}/logout`,
      );
      return response.data;
    } catch (e) {
      console.error('Failed logging out', e);
      throw new Error(CommonErrors.FAILED_LOGOUT);
    }
  }

  async sendOtpLogin(
    email: string,
    recaptcha?: string,
    source: 'login' | 'register' = 'login',
  ) {
    try {
      const response = await this.httpService.post<
        { email: string; recaptcha?: string; source?: 'login' | 'register' },
        BaseResponse<{ sent: boolean }>
      >(`${this.path}/otp/send`, { email, recaptcha, source });
      return response.data;
    } catch (e) {
      this.handleLoginError(e);
    }
  }

  async verifyOtpLogin(
    email: string,
    code: string,
    source: 'login' | 'register',
  ) {
    try {
      const response = await this.httpService.post<
        { email: string; code: string; source: 'login' | 'register' },
        BaseResponse<string>
      >(`${this.path}/otp/verify`, { email, code, source });
      return response.data;
    } catch (e) {
      this.handleLoginError(e);
    }
  }
}
